import {
    Box,
    Grid,
    Typography,
    TextField,
    Button,
    IconButton,
    Tab,
    Tabs,
} from "@mui/material";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { calculateDas28, postDas28 } from "../../connections/postApi";
import { primaryBtnTeal } from "../../style/PatientDetailsStyle";
import { showToast } from "../../utils/toastUtil";
import { useSelector } from "react-redux";
import { primaryBtn, textfieldText } from "../../style/AppointmentStyle";
import CustomModal from "../CustomModal/CustomModal";
import HistoryDas28 from "./HistoryDas28";
import HistoryIcon from '@mui/icons-material/History';
import UseFetchDas28History from "../../helper/UseFetchDas28History";


const DAS28 = ({ onClose, patient }) => {

    const [form, setForm] = useState({
        tjc28: "",
        sjc28: "",
        global_health: "",
        esr: "",
        crp: "",
    });
    const [dasCalculatorHistoryModal, setDasCalculatorHistoryModal] = useState(false);
    const [result, setResult] = useState({
        esr_score: null,
        crp_score: null,
    });

    const [loading, setLoading] = useState(false);

    const isFormValid = () => {
        return (
            form.tjc28.trim() !== "" &&
            form.sjc28.trim() !== "" &&
            form.global_health.trim() !== "" &&
            form.esr.trim() !== "" &&
            form.crp.trim() !== ""
        );
    };

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const { data: historyList } = UseFetchDas28History(patient?.id);


    // ✅ Debounced API Call 
    useEffect(() => {
        if (!isFormValid()) return;   // stop if any field empty

        const timeout = setTimeout(() => {
            calculateDAS28();
        }, 600);

        return () => clearTimeout(timeout);
    }, [form.tjc28, form.sjc28, form.global_health, form.esr, form.crp]);


    const calculateDAS28 = async () => {
        try {
            setLoading(true);
            const payload = {
                tjc28: form.tjc28,
                sjc28: form.sjc28,
                global_health: form.global_health,
                esr: form.esr,
                crp: form.crp,
            }

            const res = await calculateDas28(payload, patient?.id)
            // const data = await res.json();

            setResult({
                esr_score: res?.das28_esr_score || "—",
                crp_score: res?.das28_crp_score || "—",
                activity_level: res?.activity_level || "—",
                activity_level_description: res?.activity_level_description || "—",
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getActivityStatus = (score) => {
        if (!score || isNaN(score)) return null;

        const val = parseFloat(score);

        if (val < 2.6) return "Remission (< 2.6)";
        if (val >= 2.6 && val <= 3.2) return "Low (2.6 – 3.2)";
        if (val > 3.2 && val <= 5.1) return "Moderate (3.2 – 5.1)";
        if (val > 5.1) return "High (> 5.1)";

        return null;
    };

    const appointmentData = useSelector(
        (state) => state.prescription.appointmentData
    );

    const handleSaveScore = async () => {

        // ✅ Validation: Check empty fields
        if (
            !form.tjc28 ||
            !form.sjc28 ||
            !form.esr ||
            !form.crp ||
            !form.global_health
        ) {
            showToast("Please fill all fields before saving!", "ERROR");
            return;  // ❌ Stop execution
        }

        if (!appointmentData?.id) {
            showToast("Appointment not found for select patient", 'ERROR')
        }


        try {
            const payload = {
                tjc28: form.tjc28,
                sjc28: form.sjc28,
                esr: form.esr,
                crp: form.crp,
                global_health: form.global_health,
                patient_id: patient?.id,
                appointment_id: appointmentData?.id,
            };

            const res = await postDas28(payload);

            if (res?.status?.status === "SUCCESS") {
                showToast("DAS28 score saved successfully!", "SUCCESS");
                onClose();
            } else {
                showToast(res?.status?.message || "Failed to save score", "ERROR");
            }
        } catch (error) {
            console.error(error);
            showToast("Something went wrong!", "ERROR");
        }
    };

    const [activeTab, setActiveTab] = useState(0);

    return (
        <Box sx={{
            height: "90vh",
            display: "flex",
            flexDirection: "column",
            p: 1,
            mt: -1,
        }}>
            {/* HEADER */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#fff",
                }}
            >
                <Box />

                <Box display="flex" flexDirection="column" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <CalculateOutlinedIcon sx={{ color: "neutral.teal" }} />
                        <Typography fontWeight={700}>DAS28</Typography>
                    </Box>
                </Box>

                <IconButton onClick={onClose}>
                    <CloseIcon sx={{ color: "red" }} />
                </IconButton>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ width: '100%' }}
            >
                <Tab label="Calculator" sx={{ fontFamily: 'Lato', textTransform: 'none' }} />
                <Tab label="History" sx={{ fontFamily: 'Lato', textTransform: 'none' }} />
            </Tabs>


            <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: "flex", alignItems: 'center', gap: 2 }}>
                    <Typography variant="heading2_16">
                        Patient: <Typography variant="heading2_14">{patient?.full_name}</Typography></Typography>
                    <Typography variant="heading2_16">
                        ID: <Typography variant="heading2_14">{patient?.uid}</Typography></Typography>

                </Box>

                {/* {historyList?.payload?.das28_scores && historyList?.payload?.das28_scores?.length > 0 &&
                    <Button sx={{ ...primaryBtn, gap: 1, alignItems: 'center', }} onClick={() => setDasCalculatorHistoryModal(true)}><HistoryIcon color="white" /><Typography>
                        View  Score history</Typography>
                    </Button>
                } */}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    pr: 1,
                    mt: 0,
                }}
            >
                {activeTab === 0 &&
                    <>

                        {/* BODY */}
                        <Grid container spacing={3} mt={2} mb={3}>

                            <Grid item xs={12} md={6}>
                                <Typography variant="heading2_16">
                                    Joint Assessment</Typography>

                                <Typography variant="body2" mt={1}>
                                    Tender Joint Count (TJC28)
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Number of tender joints (0-28)"
                                    size="small"
                                    value={form.tjc28}
                                    onChange={(e) => handleChange("tjc28", e.target.value)}
                                    sx={{ ...textfieldText, my: 1 }}
                                />

                                <Typography variant="body2">
                                    Swollen Joint Count (SJC28)
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Number of swollen joints (0-28)"
                                    size="small"
                                    value={form.sjc28}
                                    onChange={(e) => handleChange("sjc28", e.target.value)}
                                    sx={textfieldText}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="heading2_16">
                                    Laboratory Values</Typography>

                                <Typography variant="body2" mt={1}>ESR (mm/h)</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Erythrocyte Sedimentation Rate"
                                    value={form.esr}
                                    onChange={(e) => handleChange("esr", e.target.value)}
                                    sx={{ ...textfieldText, my: 1 }}
                                />

                                <Typography variant="body2">CRP (mg/L)</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="C-Reactive Protein"
                                    value={form.crp}
                                    onChange={(e) => handleChange("crp", e.target.value)}
                                    sx={textfieldText}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="heading2_16">
                                    Global Health Assessment
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Patient global assessment (0-100mm VAS)"
                                    value={form.global_health}
                                    onChange={(e) =>
                                        handleChange("global_health", e.target.value)
                                    }
                                    sx={textfieldText}
                                />
                            </Grid>
                        </Grid>

                        {/* RESULTS */}
                        <Typography variant="heading2_16" mt={2}>
                            Calculated Scores
                        </Typography>

                        <Grid container spacing={2} mt={0}>
                            {/* ESR BOX */}
                            <Grid item xs={6}>
                                <Box sx={{ border: "1px solid #00ACC1", borderRadius: "6px" }}>
                                    <Box sx={{ bgcolor: "#00ACC1", color: "#fff", p: 1, fontFamily: 'Lato' }}>
                                        DAS28 (ESR)
                                    </Box>

                                    <Box sx={{ p: 3, textAlign: "center" }}>
                                        <Typography variant="heading1">
                                            {loading ? "Calculating..." : result?.esr_score ?? "—"}
                                        </Typography>

                                        {result?.esr_score && (
                                            <Typography mt={1} fontSize={14} color="#555">
                                                {getActivityStatus(result.esr_score)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>

                            {/* CRP BOX */}
                            <Grid item xs={6}>
                                <Box sx={{ border: "1px solid #2E7D32", borderRadius: "6px" }}>
                                    <Box sx={{ bgcolor: "#2E7D32", color: "#fff", p: 1, fontFamily: 'Lato' }}>
                                        DAS28 (CRP)
                                    </Box>

                                    <Box sx={{ p: 3, textAlign: "center" }}>
                                        <Typography variant="heading1">
                                            {loading ? "Calculating..." : result?.crp_score ?? "—"}
                                        </Typography>

                                        {result?.crp_score && (
                                            <Typography mt={1} fontSize={14} color="#555">
                                                {getActivityStatus(result.crp_score)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>

                        </Grid>

                        <Typography mt={2} fontSize={12} color="gray">
                            Activity Levels: Remission &lt; 2.6 | Low 2.6–3.2 | Moderate 3.2–5.1 | High &gt; 5.1
                        </Typography>


                    </>
                }

                {activeTab === 1 &&
                    <HistoryDas28
                        onClose={() => setDasCalculatorHistoryModal(false)}
                        patient={patient}
                    />
                }

            </Box>
            {activeTab === 0 &&
                <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
                    <Button
                        variant="contained"
                        sx={primaryBtnTeal}
                        onClick={handleSaveScore}
                    >
                        Save Score
                    </Button>
                </Box>}
        </Box>
    );
};

export default DAS28;
